import React, { useState, useEffect } from 'react';
import {
  Loader2, Users, CreditCard, TrendingUp, BarChart3, AlertCircle, ChevronDown, ChevronRight,
} from 'lucide-react';
import { API_BASE_URL, EDGE_FUNCTIONS_URL } from '../../constants/api';

interface OrgMember {
  user_id: string;
  user_name: string;
  user_email: string;
  tokens_used: number;
  last_validation: string | null;
}

interface OrgUsage {
  id: string;
  name: string;
  slug: string;
  plan_type: string;
  tokens_purchased: number;
  tokens_used: number;
  tokens_remaining: number;
  subscription_status: string | null;
  member_count: number;
  members: OrgMember[];
}

interface Summary {
  active_subscriptions: number;
  total_orgs: number;
  total_tokens_sold: number;
  total_tokens_used: number;
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
};

const formatTokens = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
};

const AdminUsagePage: React.FC = () => {
  const [orgs, setOrgs] = useState<OrgUsage[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${EDGE_FUNCTIONS_URL}/payments/admin/all-orgs-usage`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to load');
        if (data.success) {
          setOrgs(data.data.organizations);
          setSummary(data.data.summary);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="flex-1 flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  if (error) {
    return (
      <div className="flex-1 flex flex-col bg-gray-50 overflow-auto p-6">
        <div className="max-w-3xl mx-auto w-full">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div><p className="font-medium text-red-800">Error</p><p className="text-sm text-red-700 mt-1">{error}</p></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-auto">
      <div className="max-w-6xl mx-auto w-full p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Analytics</h1>
          <p className="text-gray-500 mt-1">Organization-level billing and token usage overview.</p>
        </div>

        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: CreditCard, color: 'green', label: 'Active Subscriptions', value: summary.active_subscriptions },
              { icon: Users, color: 'blue', label: 'Total Organizations', value: summary.total_orgs },
              { icon: TrendingUp, color: 'purple', label: 'Total Tokens Sold', value: formatTokens(summary.total_tokens_sold) },
              { icon: BarChart3, color: 'yellow', label: 'Total Tokens Used', value: formatTokens(summary.total_tokens_used) },
            ].map((card, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className={`p-2 bg-${card.color}-100 rounded-lg`}>
                    <card.icon className={`w-5 h-5 text-${card.color}-600`} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">{card.label}</div>
                    <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Orgs Table with Expandable Rows */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600 w-8"></th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Organization</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Plan</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Members</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Purchased</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Used</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Remaining</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Usage %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orgs.map(org => {
                const purchased = Number(org.tokens_purchased) || 0;
                const used = Number(org.tokens_used) || 0;
                const remaining = Number(org.tokens_remaining) || 0;
                const pct = purchased > 0 ? ((used / purchased) * 100).toFixed(1) : '—';
                const isExpanded = expandedOrg === org.id;

                return (
                  <React.Fragment key={org.id}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedOrg(isExpanded ? null : org.id)}>
                      <td className="px-4 py-3">
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{org.name}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          org.plan_type === 'none' ? 'bg-gray-100 text-gray-500'
                          : (org.plan_type === 'monthly' || org.plan_type === 'yearly') ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                        }`}>{org.plan_type === 'none' ? 'No Plan' : org.plan_type}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{org.member_count}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatTokens(purchased)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatTokens(used)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatTokens(remaining)}</td>
                      <td className="px-4 py-3 text-right">
                        {pct !== '—' ? (
                          <span className={`font-medium ${Number(pct) > 80 ? 'text-red-600' : Number(pct) > 50 ? 'text-yellow-600' : 'text-green-600'}`}>{pct}%</span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                    {isExpanded && org.members && org.members.length > 0 && (
                      <tr>
                        <td colSpan={8} className="bg-gray-50 px-8 py-3">
                          <div className="text-xs font-medium text-gray-500 uppercase mb-2">Members</div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500">
                                <th className="text-left py-1">Name</th>
                                <th className="text-left py-1">Email</th>
                                <th className="text-right py-1">Tokens Used</th>
                                <th className="text-left py-1">Last Validation</th>
                              </tr>
                            </thead>
                            <tbody>
                              {org.members.map(m => (
                                <tr key={m.user_id} className="text-gray-700">
                                  <td className="py-1">{m.user_name}</td>
                                  <td className="py-1">{m.user_email}</td>
                                  <td className="py-1 text-right font-medium">{formatTokens(Number(m.tokens_used))}</td>
                                  <td className="py-1 text-gray-500">{m.last_validation ? new Date(m.last_validation).toLocaleString() : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminUsagePage;
